import { FetchOrgOverviewV2SuccessAction } from './../actions/orgOverview';
import { OrganizationEntity } from '../types';
import { LogoutSuccessAction, LOGOUT_SUCCESS } from '../actions/login';
import { keyBy, uniq } from 'lodash';
import { combineReducers } from 'redux';
import { OrgStateUpdatedAction, ORG_STATE_UPDATED } from '../actions/organization';
import {
    CreateIntegrationSuccessAction,
    CREATE_INTEGRATION_REQUEST_SUCCESS,
    DELETE_INTEGRATION_REQUEST_SUCCESS,
    DeleteIntegrationSuccessAction,
    REFRESH_INTEGRATION_REQUEST_SUCCESS,
    RefreshIntegrationSuccessAction,
} from '../actions/integrations';
import { ClearStateAction, CLEAR_STATE, FetchStateSuccessAction, FETCH_STATE_SUCCESS } from '../actions/state';
import { UpdateOrgSettingsSuccessAction, UPDATE_ORG_SETTINGS_SUCCESS } from '../actions/orgSettings';

interface OrgsById {
    [id: string]: OrganizationEntity;
}

type OrgsActionTypes =
    | FetchStateSuccessAction
    | OrgStateUpdatedAction
    | CreateIntegrationSuccessAction
    | RefreshIntegrationSuccessAction
    | DeleteIntegrationSuccessAction
    | LogoutSuccessAction
    | UpdateOrgSettingsSuccessAction
    | FetchOrgOverviewV2SuccessAction
    | ClearStateAction;

function createOrgsById(state, action) {
    const orgEntities = action.payload.organizations.map((org) => {
        const {
            id,
            orgId,
            name,
            users,
            orgImageUrl,
            integrationProviders,
            trialDaysRemaining,
            allowedDomains,
            mergeAccountsWithAllowedDomainEnabled,
            addNewUsersToAppsSettingEnabled,
            disableAIForAppStoreReviews,
            disableAIForReleaseNotes,
            isEnabledAIReleaseNotesFeature,
            subscriptionInfo,
            orgSettings,
            ssoSamlConfig,
            apiKeys,
            createdAt,
        } = org;

        return {
            id,
            orgId,
            name,
            createdAt,
            orgImageUrl,
            users,
            integrationProviders,
            trialDaysRemaining,
            allowedDomains,
            mergeAccountsWithAllowedDomainEnabled,
            addNewUsersToAppsSettingEnabled,
            disableAIForAppStoreReviews,
            disableAIForReleaseNotes,
            isEnabledAIReleaseNotesFeature,
            subscriptionInfo,
            orgSettings,
            ssoSamlConfig,
            apiKeys,
        };
    });

    const orgsById = keyBy(orgEntities, (org) => org.id);

    return {
        ...state,
        ...orgsById,
    };
}

function updateOrgById(state: OrgsById, org: OrganizationEntity) {
    return {
        ...state,
        [org.id]: {
            ...org,
        },
    };
}

function orgsById(state: OrgsById = {}, action: OrgsActionTypes): OrgsById {
    switch (action.type) {
        case FETCH_STATE_SUCCESS:
            return createOrgsById(state, action);
        case ORG_STATE_UPDATED:
        case UPDATE_ORG_SETTINGS_SUCCESS:
            return updateOrgById(state, action.payload);
        case DELETE_INTEGRATION_REQUEST_SUCCESS:
        case CREATE_INTEGRATION_REQUEST_SUCCESS:
        case REFRESH_INTEGRATION_REQUEST_SUCCESS:
            return updateOrgById(state, action.payload.orgEntity);
        case LOGOUT_SUCCESS:
        case CLEAR_STATE:
            return {};
        default:
            return state;
    }
}

function allIds(state: string[] = [], action: OrgsActionTypes): string[] {
    switch (action.type) {
        case FETCH_STATE_SUCCESS:
            return action.payload.organizations.map((org) => org.id);
        case ORG_STATE_UPDATED:
        case UPDATE_ORG_SETTINGS_SUCCESS:
            return uniq([...state, action.payload.id]);
        case LOGOUT_SUCCESS:
        case CLEAR_STATE:
            return [];
        default:
            return state;
    }
}

const orgsReducer = combineReducers({
    byId: orgsById,
    allIds: allIds,
});

export default orgsReducer;
